import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What is the primary focus of spiritual wellness according to this module?",
    options: [
      "Religious practices and rituals",
      "Finding meaning, purpose, and connection in life",
      "Meditation and breathing exercises",
      "Following spiritual leaders"
    ],
    correctAnswer: 1,
    explanation: "Spiritual wellness is about finding meaning, purpose, and connection in life. It's a personal journey that goes beyond religious practices to include a sense of belonging and understanding your place in the world."
  },
  {
    question: "According to the module, spiritual wellness involves:",
    options: [
      "Only religious beliefs and practices",
      "A sense of peace, purpose, and connection to something greater",
      "Strict dietary and lifestyle rules",
      "Avoiding all worldly activities"
    ],
    correctAnswer: 1,
    explanation: "Spiritual wellness encompasses a sense of peace, purpose, and connection to something greater than yourself. It can be expressed through various paths and doesn't require specific religious affiliations."
  },
  {
    question: "What role does spiritual wellness play in overall well-being?",
    options: [
      "It replaces the need for physical and mental health",
      "It provides a foundation for resilience and coping with life's challenges",
      "It's only important for religious people",
      "It has no impact on daily life"
    ],
    correctAnswer: 1,
    explanation: "Spiritual wellness provides a foundation for resilience and coping with life's challenges. It helps you navigate difficulties with greater strength and find meaning even in difficult circumstances."
  },
  {
    question: "The module suggests that spiritual wellness can be cultivated through:",
    options: [
      "Only formal religious practices",
      "Personal reflection, nature connection, and meaningful relationships",
      "Ignoring life's difficulties",
      "Following strict spiritual rules"
    ],
    correctAnswer: 1,
    explanation: "Spiritual wellness can be cultivated through personal reflection, connecting with nature, building meaningful relationships, and engaging in activities that bring a sense of peace and purpose."
  },
  {
    question: "What is the relationship between spiritual wellness and life satisfaction?",
    options: [
      "They are completely unrelated",
      "Spiritual wellness often leads to greater life satisfaction and fulfillment",
      "Life satisfaction comes only from material success",
      "Spiritual wellness decreases life satisfaction"
    ],
    correctAnswer: 1,
    explanation: "Research shows that spiritual wellness often leads to greater life satisfaction and fulfillment. People who feel connected to something greater and have a sense of purpose tend to report higher levels of happiness and well-being."
  }
];

const SpiritualModule1QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM1');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/spiritual-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 1 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check">✓</span>
                <span>The foundations of spiritual wellness</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Personal meaning and purpose</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Connection to something greater</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your spiritual journey by exploring connectedness!</p>
            <p>Ready to deepen your understanding of spiritual wellness?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Spiritual Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left spiritual-panel">
          <div className="module-header">
            <h1>Module 1 Quiz</h1>
            <p className="module-subtitle">Understanding Spiritual Wellness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentQuestion === 0 ? handleExit : handleBack}
            >
              {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
            </button>

            {currentQuestion < quizQuestions.length - 1 ? (
              <button
                className="btn-primary"
                onClick={handleNext}
                disabled={selectedAnswer === null}
              >
                Next Question
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
                disabled={selectedAnswer === null}
              >
                Complete Quiz
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule1QuizPage;

